import React, { useEffect, useState } from "react";
import { Clock } from "lucide-react";

const HARI = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

// Return object {jam, menit, detik, hari, tanggal, bulan, tahun} in WIB (Asia/Jakarta)
export const getWIBParts = () => {
  const now = new Date();
  try {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Jakarta",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false, weekday: "short",
    });
    const parts = fmt.formatToParts(now);
    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    const wdMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    let hour = get("hour");
    if (hour === "24") hour = "00"; // en-CA quirk: midnight = 24
    return {
      jam: hour.padStart(2, "0"),
      menit: get("minute").padStart(2, "0"),
      detik: get("second").padStart(2, "0"),
      hari: HARI[wdMap[get("weekday")] ?? 0],
      tanggal: parseInt(get("day"), 10),
      bulan: BULAN[parseInt(get("month"), 10) - 1] || "",
      tahun: get("year"),
    };
  } catch (e) {
    // Fallback: manual UTC+7 calculation
    const wib = new Date(now.getTime() + (7 * 60 - now.getTimezoneOffset() * -1) * 60 * 1000 + now.getTimezoneOffset() * 60 * 1000);
    const wibMs = now.getTime() + 7 * 3600 * 1000 - (-now.getTimezoneOffset()) * 60 * 1000;
    const d = new Date(wibMs);
    return {
      jam: String(d.getUTCHours()).padStart(2, "0"),
      menit: String(d.getUTCMinutes()).padStart(2, "0"),
      detik: String(d.getUTCSeconds()).padStart(2, "0"),
      hari: HARI[d.getUTCDay()],
      tanggal: d.getUTCDate(),
      bulan: BULAN[d.getUTCMonth()],
      tahun: String(d.getUTCFullYear()),
    };
  }
};

const WIBClock = ({ compact = false, className = "" }) => {
  const [p, setP] = useState(getWIBParts());
  useEffect(() => {
    const t = setInterval(() => setP(getWIBParts()), 1000);
    return () => clearInterval(t);
  }, []);

  const jam = `${p.jam}:${p.menit}:${p.detik}`;
  const tanggal = `${p.hari}, ${p.tanggal} ${p.bulan} ${p.tahun}`;

  if (compact) {
    return (
      <div className={`flex items-center gap-2 text-sm text-emerald-800 ${className}`}>
        <Clock className="h-4 w-4" />
        <span className="font-semibold tabular-nums">{jam}</span>
        <span className="text-xs text-slate-500 hidden md:inline">WIB</span>
      </div>
    );
  }

  return (
    <div className={`rounded-xl bg-white/70 backdrop-blur border border-emerald-100 px-4 py-3 flex items-center gap-3 ${className}`}>
      <div className="h-10 w-10 rounded-lg brand-gradient text-white grid place-items-center shadow-md">
        <Clock className="h-5 w-5" />
      </div>
      <div className="leading-tight">
        <div className="text-xs uppercase tracking-wide text-slate-500">Waktu Indonesia Barat</div>
        <div className="text-lg font-bold tabular-nums text-slate-800">{jam} <span className="text-xs font-medium text-emerald-700">WIB</span></div>
        <div className="text-xs text-slate-600">{tanggal}</div>
      </div>
    </div>
  );
};

export default WIBClock;
